<template>
  <div>
    <Popup :modelValue="showAutoPopulatePopup" popupHeader="Add your Back-Up Care info?">
      <p>Do you want to add your Back-Up care information into your job post or create your own?</p>
      <Button value="Add my info" class="w-full mt-16" size="medium" @click="populateJobPostWithCareSessionData" />
      <Button
        value="Create my own job"
        class="w-full mt-16"
        size="medium"
        variant="outlined"
        @click="showAutoPopulatePopup = false"
      />
    </Popup>
    <ProgressBar
      :value="progress.currentStep"
      :max="progress.totalSteps"
      :content="`Step ${progress.currentStep} of ${progress.totalSteps}`"
    />
    <RouteStepper
      v-model="jobPostDraft"
      class="md:mt-24 md:w-568 px-16 mx-auto"
      :stepFilter="jobPostStepFilter"
      :autoValidateOnInit="isFromRegistration"
      @progress="progress = $event"
    />
  </div>
</template>

<script lang="ts">
import RouteStepper from '@/components/route_stepper/RouteStepper.vue';
import { PREPOPULATE_RESERVATION_QUERY } from '@/graphql/backup_care/queries';
import { extractJobDataFromCareSessions } from './helpers/backupCareJobPostHelpers';
import Popup from '@/components/popup/Popup.vue';
import Button from '@/components/button/Button.vue';
import { JobPostDraft } from './types';
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import { CareType } from '@/types/care_type';
import { Requirements } from '@/store/modules/job/types';

interface JobPostData {
  jobPostDraft: JobPostDraft;
  showAutoPopulatePopup: boolean;
  reservation: { scCareSessions: any[] } | null;
  progress: { currentStep: number; totalSteps: number };
}

export default {
  name: 'JobPost',
  components: { RouteStepper, Popup, Button, ProgressBar },
  data(): JobPostData {
    return {
      jobPostDraft: {
        title: '',
        description: '',
        ageGroups: [],
        requirements: [],
        responsibilities: [],
        languages: [],
        location: null,
        locationDetails: null,
        numberOfChildren: null,
        paymentRate: null,
        schedule: null,
        aiGenerated: false,
        zipCode: '',
        isPrepopulatedFromReservation: false,
        scheduleType: null,
        URLSearchParams: null,
      },
      showAutoPopulatePopup: false,
      reservation: null,
      progress: { currentStep: 0, totalSteps: 0 },
    };
  },
  computed: {
    isFromRegistration(): boolean {
      return this.$route.query.source === 'registration';
    },
  },
  watch: {
    reservation(): void {
      if (this.reservation !== null) {
        // if reservation data is available, show the auto-populate popup
        this.showAutoPopulatePopup = true;
      }
    },
  },
  created() {
    this.findAndSetURLSearchParams();
    this.findAndSetReservation();
  },
  methods: {
    findAndSetURLSearchParams(): void {
      // Set the URLSearchParams from the current document location
      this.jobPostDraft.URLSearchParams = new URLSearchParams(document.location.search);
    },
    findAndSetReservation(): void {
      const reservationIdQueryParam = this.jobPostDraft.URLSearchParams?.get('reservationId') || null;
      if (reservationIdQueryParam) {
        // fetch reservation data from the query parameter
        this.setReservationFromQueryParam(reservationIdQueryParam);
      } else if (this.$store.getters['account/getAccount'].isCorporate()) {
        // fetch the most recent active reservation
        this.setReservationFromMostRecentActive();
      }
    },
    async getReservationsQuery(): Promise<any[]> {
      try {
        const { data } = await this.$apollo.query({
          query: PREPOPULATE_RESERVATION_QUERY,
        });
        return data?.corp?.backupCare?.bhReservationRequests || [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    async setReservationFromQueryParam(reservationId: string): Promise<void> {
      // Fetch reservation data from the query parameter
      // and set it to the reservation data
      const reservations = await this.getReservationsQuery();
      const reservationFromId = reservations.find((reservation: any) => reservation?.bhReservationRequestId === reservationId);
      if (!reservationFromId) {
        console.warn('No reservation found with the given ID');
        return;
      }

      this.reservation = reservationFromId;
    },
    async setReservationFromMostRecentActive(): Promise<void> {
      // Fetch the most recent active reservation
      // and set it to the reservation data
      const reservations = await this.getReservationsQuery();
      if (!reservations || reservations.length === 0) {
        console.warn('No reservations found');
        return;
      }
      const currentDateISO: string = new Date().toISOString();
      const mostRecentActiveReservation = reservations.reduce((prevMostRecentReservation, curReservation) => {
        const isStartDateExpired: boolean = curReservation.expiresAt < currentDateISO;
        const isAMoreRecentlyCreatedReservation: boolean =
          curReservation.createdAt > prevMostRecentReservation?.createdAt || prevMostRecentReservation === null;
        const hasValidCareSession: boolean = curReservation.scCareSessions[0].start > currentDateISO;

        if (!isStartDateExpired && isAMoreRecentlyCreatedReservation && hasValidCareSession) {
          return curReservation; // update prevMostRecentReservation to the current reservation because it is more recent and valid
        }
        return prevMostRecentReservation;
      }, null);

      if (!mostRecentActiveReservation) {
        return;
      }

      const isCareSessionValid: boolean = mostRecentActiveReservation.scCareSessions[0].start > currentDateISO;
      const isCompanionCare: boolean = mostRecentActiveReservation.scCareSessions[0].careType === CareType.COMPANION_CARE;

      if (isCareSessionValid && !isCompanionCare) {
        this.reservation = mostRecentActiveReservation;
      }
    },
    populateJobPostWithCareSessionData(): void {
      const careSessionData = this.reservation?.scCareSessions || [];

      if (careSessionData.length === 0) {
        console.warn('No reservation data available to populate the job post');
        this.showAutoPopulatePopup = false;
        return;
      }

      const extractedJobData: JobPostDraft = extractJobDataFromCareSessions(careSessionData);
      const { scheduleType, ageGroups, location, zipCode, schedule, numberOfChildren, locationDetails } = extractedJobData;

      this.jobPostDraft = {
        ...this.jobPostDraft,
        scheduleType,
        ageGroups,
        location,
        locationDetails,
        zipCode,
        schedule,
        numberOfChildren,
        isPrepopulatedFromReservation: true,
      };

      this.showAutoPopulatePopup = false;
    },
    jobPostStepFilter(step) {
      if (step.name === this.$route.name) {
        // Keep current step in the filtered steps to avoid refresh issues
        return true;
      }
      const hasPetRequirement = this.jobPostDraft?.requirements?.includes(Requirements.COMFORTABLE_WITH_PETS);

      if (step.name === 'job-post-pet-details') {
        return hasPetRequirement;
      }

      return true;
    },
  },
};
</script>
